// src/pages/CourseTime/utils/sortSegments.js

/**
 * Sorts segment keys in the correct order:
 * Seg1, Seg1_1 (pre-assessments), then ALL SLOs, then Seg3, Seg4, Seg4_1 (post-assessments), Seg5...
 *
 * Order: Seg1 → Seg1_1 → SLO1, SLO2, SLO3... → Seg3 → Seg4 → Seg4_1 → Seg5...
 *
 * This ensures that:
 * - Seg1 (Introduction) comes first
 * - Seg1_1 (Pre-assessments, was Seg2) comes after Seg1
 * - ALL SLO segments come after Seg1_1
 * - Seg3, Seg4 come after SLOs
 * - Seg4_1 (Post-assessments, was Seg6) comes after Seg4 but before Seg5
 */
export const sortSegmentKeys = (segmentKeys) => {
  return segmentKeys.sort((a, b) => {
    // Extract the type (Seg or SLO) and number from each key
    const parseKey = (key) => {
      // Match patterns like: Seg1, Seg1_1, Seg4_1, SLO1, SLO2, Intro, Closing
      const segMatch = key.match(/^Seg(\d+)(?:_(\d+))?$/);
      const sloMatch = key.match(/^SLO(\d+)$/);

      if (key === 'Intro') {
        return { type: 'Intro', mainNumber: 0, subNumber: 0, isValid: true };
      }
      if (key === 'Closing') {
        return { type: 'Closing', mainNumber: 0, subNumber: 0, isValid: true };
      }

      if (segMatch) {
        const mainNumber = parseInt(segMatch[1], 10);
        const subNumber = segMatch[2] ? parseInt(segMatch[2], 10) : 0;
        return {
          type: 'Seg',
          mainNumber: mainNumber,
          subNumber: subNumber,
          isValid: true
        };
      } else if (sloMatch) {
        return {
          type: 'SLO',
          mainNumber: parseInt(sloMatch[1], 10),
          subNumber: 0,
          isValid: true
        };
      }
      // If it doesn't match the pattern, return it as-is
      return { type: key, mainNumber: 0, subNumber: 0, isValid: false };
    };

    const parsedA = parseKey(a);
    const parsedB = parseKey(b);

    // Handle non-standard keys (push to end)
    if (!parsedA.isValid && !parsedB.isValid) return a.localeCompare(b);
    if (!parsedA.isValid) return 1;
    if (!parsedB.isValid) return -1;

    // Assign sort priority based on segment type and number
    const getPriority = (parsed) => {
      if (parsed.type === 'Intro') {
        // Comes before everything, including Seg1
        return 0;
      } else if (parsed.type === 'Closing') {
        // Comes after everything else
        return 100000;
      } else if (parsed.type === 'Seg') {
        if (parsed.mainNumber === 1) {
          // Seg1 = priority 1, Seg1_1 = priority 1.5
          return parsed.subNumber === 0 ? 1 : 1.5;
        } else if (parsed.mainNumber === 4 && parsed.subNumber === 1) {
          // Seg4_1 (post-assessments, was Seg6) = priority 1004.5 (after Seg4, before Seg5)
          return 1004.5;
        } else {
          // Seg3, Seg4, Seg5, Seg7+ = priority 1000 + mainNumber + (subNumber * 0.1)
          return 1000 + parsed.mainNumber + (parsed.subNumber * 0.1);
        }
      } else if (parsed.type === 'SLO') {
        // SLOs = priority 100-999 (between Seg1_1 and Seg3+)
        return 100 + parsed.mainNumber;
      }
      return 10000; // Unknown types go to end
    };

    const priorityA = getPriority(parsedA);
    const priorityB = getPriority(parsedB);

    return priorityA - priorityB;
  });
};

/**
 * Creates a new content object with sorted segment keys
 */
export const sortSessionContent = (content) => {
  if (!content || typeof content !== 'object') {
    return content;
  }

  const sortedKeys = sortSegmentKeys(Object.keys(content));
  const sortedContent = {};

  sortedKeys.forEach(key => {
    sortedContent[key] = content[key];
  });

  return sortedContent;
};

